// src/pages/Organizations/OrganizationAdminDashboard/index.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  CardHeader,
  Button,
  Divider,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Container
} from '@mui/material';
import {
  Refresh,
  Group,
  Event,
  CardMembership,
  ConfirmationNumber,
  TrendingUp,
  Business,
  PersonAdd,
  ArrowUpward,
  ArrowDownward,
  AdminPanelSettings
} from '@mui/icons-material';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs,
  getDoc,
  doc, 
  Timestamp 
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { useUser } from '../../../contexts/UserContext';
import { useNavigate } from 'react-router-dom';

// Define interfaces for metrics
interface OrganizationMetrics {
  totalOrganizations: number;
  activeOrganizations: number;
  totalMembers: number;
  totalTrips: number;
  totalEvents: number;
  totalSubscriptions: number;
  totalTicketsSold: number;
  totalRevenue: number;
  organizationsCreatedByPersonalAccounts: number;
  organizationsCreatedByOrgAccounts: number;
  organizationsByDay: { date: string; count: number }[];
}

interface RecentActivity {
  id: string;
  organizationId: string;
  organizationName: string;
  userId: string;
  userName: string;
  userPhotoURL: string | null;
  action: string;
  entityType: string; // 'trip', 'event', 'subscription', etc.
  entityId: string;
  entityName: string;
  timestamp: any;
}

interface RecentOrganization {
  id: string;
  name: string;
  logo: string | null;
  createdBy: string;
  createdByName: string;
  createdByType: 'personal' | 'organization';
  createdByOrgId?: string;
  createdByOrgName?: string;
  memberCount: number;
  createdAt: any;
}

// Define types for organization data
interface Organization {
  id: string;
  name?: string;
  logo?: string;
  createdAt?: {
    toDate: () => Date;
  };
  createdByType?: 'organization' | 'personal';
  createdByOrgId?: string;
  createdBy?: string;
  active?: boolean;
}

interface Activity {
  id: string;
  organizationId?: string;
  userId?: string;
  type: string;
  details: string;
  timestamp: {
    toDate: () => Date;
  };
}

interface UserData {
  displayName?: string;
  email?: string;
  photoURL?: string;
}

// Main component
const OrganizationAdminDashboard: React.FC = () => {
  const [metrics, setMetrics] = useState<OrganizationMetrics | null>(null);
  const [recentActivities, setRecentActivities] = useState<RecentActivity[]>([]);
  const [recentOrganizations, setRecentOrganizations] = useState<RecentOrganization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(false);
  const { currentUser } = useUser();
  const navigate = useNavigate();

  const SUPER_ADMIN_EMAIL = 'emmanueljones05@gmail.com';

  // Check if current user is super admin
  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    if (currentUser.email && currentUser.email === SUPER_ADMIN_EMAIL) {
       setIsSuperAdmin(true);
       fetchDashboardData();
    } else {
       setError('Unauthorized access. You do not have Super Admin privileges.');
       setLoading(false);
    }
  }, [currentUser, navigate]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch organizations metrics
      await fetchOrganizationMetrics();
      
      // Fetch recent activities
      await fetchRecentActivities();
      
      // Fetch recent organizations
      await fetchRecentOrganizations();
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
      setLoading(false);
    }
  };

  const fetchOrganizationMetrics = async () => {
    try {
      // Get all organizations
      const orgsQuery = query(collection(db, 'organizations'));
      const orgsSnapshot = await getDocs(orgsQuery);
      const organizations: Organization[] = orgsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Count organizations created by personal vs org accounts
      let personalCreated = 0;
      let orgCreated = 0;
      let totalMembers = 0;

      for (const org of organizations) {
        // Check creator type
        if (org.createdByType === 'organization' || org.createdByOrgId) {
          orgCreated++;
        } else {
          personalCreated++;
        }

        // Count members
        const membersQuery = query(collection(db, 'organizations', org.id, 'members'));
        const membersSnapshot = await getDocs(membersQuery);
        totalMembers += membersSnapshot.size;
      }

      // Get trips, events, subscriptions, and tickets
      const tripsQuery = query(collection(db, 'trips'));
      const tripsSnapshot = await getDocs(tripsQuery);
      
      const eventsQuery = query(collection(db, 'events'));
      const eventsSnapshot = await getDocs(eventsQuery);
      
      const subscriptionsQuery = query(collection(db, 'subscriptions'));
      const subscriptionsSnapshot = await getDocs(subscriptionsQuery);
      
      const ticketsQuery = query(collection(db, 'tickets'));
      const ticketsSnapshot = await getDocs(ticketsQuery);

      // Calculate ticket revenue
      let totalRevenue = 0;
      ticketsSnapshot.docs.forEach(doc => {
        const ticket = doc.data();
        totalRevenue += ticket.price || 0;
      });

      // Get organizations by day (last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const orgsByDay: { [key: string]: number } = {};
      
      organizations.forEach(org => {
        if (org.createdAt && new Date(org.createdAt.toDate()) >= thirtyDaysAgo) {
          const dateStr = new Date(org.createdAt.toDate()).toISOString().split('T')[0];
          orgsByDay[dateStr] = (orgsByDay[dateStr] || 0) + 1;
        }
      });
      
      const organizationsByDay = Object.entries(orgsByDay).map(([date, count]) => ({
        date,
        count
      })).sort((a, b) => a.date.localeCompare(b.date));

      setMetrics({
        totalOrganizations: organizations.length,
        activeOrganizations: organizations.filter(org => org.active !== false).length,
        totalMembers,
        totalTrips: tripsSnapshot.size,
        totalEvents: eventsSnapshot.size,
        totalSubscriptions: subscriptionsSnapshot.size,
        totalTicketsSold: ticketsSnapshot.size,
        totalRevenue,
        organizationsCreatedByPersonalAccounts: personalCreated,
        organizationsCreatedByOrgAccounts: orgCreated,
        organizationsByDay
      });
    } catch (error) {
      console.error('Error fetching organization metrics:', error);
      throw error;
    }
  };

  const fetchRecentActivities = async () => {
    try {
      // Get recent activities across all organizations
      const activitiesQuery = query(
        collection(db, 'activityLogs'),
        orderBy('timestamp', 'desc'),
        limit(20)
      );
      
      const activitiesSnapshot = await getDocs(activitiesQuery);
      const activities: RecentActivity[] = [];
      
      for (const docs of activitiesSnapshot.docs) {
        const activity = docs.data() as Activity;
        
        // Get organization info
        let orgName = 'Unknown Organization';
        if (activity.organizationId) {
          const orgDoc = await getDoc(doc(db, 'organizations', activity.organizationId));
          if (orgDoc.exists()) {
            orgName = (orgDoc.data() as Organization).name || 'Unknown Organization';
          }
        }
        
        // Get user info
        let userName = 'Unknown User';
        let userPhotoURL = null;
        if (activity.userId) {
          const userDoc = await getDoc(doc(db, 'users', activity.userId));
          if (userDoc.exists()) {
            const userData = userDoc.data() as UserData;
            userName = userData.displayName || userData.email || 'Unknown User';
            userPhotoURL = userData.photoURL || null;
          }
        }
        
        activities.push({
          id: docs.id,
          organizationId: activity.organizationId || '',
          organizationName: orgName,
          userId: activity.userId || '',
          userName,
          userPhotoURL,
          action: activity.type || '',
          entityType: activity.details || '',
          entityId: '',
          entityName: '',
          timestamp: activity.timestamp
        });
      }
      
      setRecentActivities(activities);
    } catch (error) {
      console.error('Error fetching recent activities:', error);
      throw error;
    }
  };

  const fetchRecentOrganizations = async () => {
    try {
      // Get recent organizations
      const orgsQuery = query(
        collection(db, 'organizations'),
        orderBy('createdAt', 'desc'),
        limit(10)
      );
      
      const orgsSnapshot = await getDocs(orgsQuery);
      const orgs: RecentOrganization[] = [];
      
      for (const docs of orgsSnapshot.docs) {
        const org = docs.data() as Organization;
        
        // Get creator info
        let createdByName = 'Unknown User';
        let createdByType: 'personal' | 'organization' = 'personal';
        let createdByOrgId = undefined;
        let createdByOrgName = undefined;
        
        if (org.createdBy) {
          const userDoc = await getDoc(doc(db, 'users', docs.id));
          if (userDoc.exists()) {
            const userData = userDoc.data() as UserData;
            createdByName = userData.displayName || userData.email || 'Unknown User';
          }
          
          if ((org as Organization).createdByOrgId) {
            createdByType = 'organization';
            createdByOrgId = (org as Organization).createdByOrgId;
            
            const creatorOrgDoc = await getDoc(doc(db, 'organizations', (org as Organization).createdByOrgId!));
            if (creatorOrgDoc.exists()) {
              createdByOrgName = (creatorOrgDoc.data() as Organization).name || 'Unknown Organization';
            }
          }
        }
        
        // Get member count
        const membersQuery = query(collection(db, 'organizations', docs.id, 'members'));
        const membersSnapshot = await getDocs(membersQuery);
        
        orgs.push({
          id: docs.id,
          name: org.name || 'Unnamed Organization',
          logo: org.logo || null,
          createdBy: org.createdBy || '',
          createdByName,
          createdByType,
          createdByOrgId,
          createdByOrgName,
          memberCount: membersSnapshot.size,
          createdAt: org.createdAt
        });
      }
      
      setRecentOrganizations(orgs);
    } catch (error) {
      console.error('Error fetching recent organizations:', error);
      throw error;
    }
  };

  const handleRefresh = () => {
    fetchDashboardData();
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !isSuperAdmin) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error || 'Unauthorized access'}</Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Organization Super Admin Dashboard
        </Typography>
        <Button 
          variant="outlined" 
          startIcon={<Refresh />} 
          onClick={handleRefresh}
        >
          Refresh Data
        </Button>
      </Box>

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Organizations</Typography>
              <Typography variant="h3">{metrics?.totalOrganizations || 0}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <Business sx={{ color: 'primary.main', mr: 1 }} />
                <Typography variant="body2">
                  {metrics?.activeOrganizations || 0} active
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Members</Typography>
              <Typography variant="h3">{metrics?.totalMembers || 0}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <Group sx={{ color: 'secondary.main', mr: 1 }} />
                <Typography variant="body2">
                  Across all organizations
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Organizations by Personal Accounts</Typography>
              <Typography variant="h3">{metrics?.organizationsCreatedByPersonalAccounts || 0}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <PersonAdd sx={{ color: 'success.main', mr: 1 }} />
                <Typography variant="body2">
                  {Math.round((metrics?.organizationsCreatedByPersonalAccounts || 0) / (metrics?.totalOrganizations || 1) * 100)}% of total
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Organizations by Org Accounts</Typography>
              <Typography variant="h3">{metrics?.organizationsCreatedByOrgAccounts || 0}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <Business sx={{ color: 'info.main', mr: 1 }} />
                <Typography variant="body2">
                  {Math.round((metrics?.organizationsCreatedByOrgAccounts || 0) / (metrics?.totalOrganizations || 1) * 100)}% of total
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Entity Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Trips</Typography>
              <Typography variant="h3">{metrics?.totalTrips || 0}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <TrendingUp sx={{ color: 'primary.main', mr: 1 }} />
                <Typography variant="body2">
                  Across all organizations
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Events</Typography>
              <Typography variant="h3">{metrics?.totalEvents || 0}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <Event sx={{ color: 'secondary.main', mr: 1 }} />
                <Typography variant="body2">
                  Across all organizations
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Subscriptions</Typography>
              <Typography variant="h3">{metrics?.totalSubscriptions || 0}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <CardMembership sx={{ color: 'success.main', mr: 1 }} />
                <Typography variant="body2">
                  Active subscription plans
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Ticket Sales</Typography>
              <Typography variant="h3">${metrics?.totalRevenue.toFixed(2) || '0.00'}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <ConfirmationNumber sx={{ color: 'info.main', mr: 1 }} />
                <Typography variant="body2">
                  {metrics?.totalTicketsSold || 0} tickets sold
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs for different data sections */}
      <Box sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="dashboard tabs">
          <Tab label="Recent Organizations" />
          <Tab label="Recent Activities" />
          <Tab label="Growth Metrics" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box>
        {/* Recent Organizations Tab */}
        {activeTab === 0 && (
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Recently Created Organizations
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Organization</TableCell>
                    <TableCell>Created By</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Members</TableCell>
                    <TableCell>Created Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentOrganizations.map((org) => (
                    <TableRow key={org.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {org.logo ? (
                            <Avatar src={org.logo} sx={{ mr: 2 }} />
                          ) : (
                            <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                              {org.name.charAt(0)}
                            </Avatar>
                          )}
                          <Typography variant="body2">{org.name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{org.createdByName}</TableCell>
                      <TableCell>
                        {org.createdByType === 'organization' ? (
                          <Chip 
                            size="small" 
                            color="secondary" 
                            label="Organization" 
                            title={org.createdByOrgName || 'Organization Account'} 
                          />
                        ) : (
                          <Chip size="small" color="primary" label="Personal" />
                        )}
                      </TableCell>
                      <TableCell>{org.memberCount}</TableCell>
                      <TableCell>
                        {org.createdAt ? new Date(org.createdAt.toDate()).toLocaleDateString() : 'Unknown'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* Recent Activities Tab */}
        {activeTab === 1 && (
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Recent Organization Activities
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Organization</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Entity</TableCell>
                    <TableCell>Time</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentActivities.map((activity) => (
                    <TableRow key={activity.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {activity.userPhotoURL ? (
                            <Avatar src={activity.userPhotoURL} sx={{ mr: 2, width: 32, height: 32 }} />
                          ) : (
                            <Avatar sx={{ mr: 2, width: 32, height: 32, bgcolor: 'primary.main' }}>
                              {activity.userName.charAt(0)}
                            </Avatar>
                          )}
                          <Typography variant="body2">{activity.userName}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{activity.organizationName}</TableCell>
                      <TableCell>
                        <Chip 
                          size="small" 
                          color="primary" 
                          label={activity.action.replace(/_/g, ' ')} 
                        />
                      </TableCell>
                      <TableCell>
                        {activity.entityType && (
                          <Typography variant="body2">
                            {activity.entityType}: {activity.entityName || activity.entityId}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {activity.timestamp ? new Date(activity.timestamp.toDate()).toLocaleString() : 'Unknown'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* Growth Metrics Tab */}
        {activeTab === 2 && (
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Organization Growth Metrics
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Organizations Created (Last 30 Days)
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>New Organizations</TableCell>
                      <TableCell>Trend</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {metrics?.organizationsByDay.map((day, index) => {
                      const prevCount = index > 0 ? metrics.organizationsByDay[index - 1].count : 0;
                      const trend = day.count > prevCount ? <ArrowUpward color="success" /> : 
                                   day.count < prevCount ? <ArrowDownward color="error" /> : null;
                      
                      return (
                        <TableRow key={day.date}>
                          <TableCell>{new Date(day.date).toLocaleDateString()}</TableCell>
                          <TableCell>{day.count}</TableCell>
                          <TableCell>{trend}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default OrganizationAdminDashboard;
